import React, {useState, useEffect, useRef} from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import MobileFriendlyIcon from "@mui/icons-material/MobileFriendly";
import {apiHelper, baseUrl} from "helpers/util";
import {notify} from "helpers/globalFunctions";
import ColumnBox from "components/ColumnBox";
import RowBox from "components/RowBox";
import SmallHeading from "components/SmallHeading";
import {Alert, Snackbar} from "@mui/material";
import html2canvas from "html2canvas";

export default function EventAttendance() {
    console.log("EventAttendance is rendering");

    const [mobileNo, setMobileNo] = useState("");
    const [getRecordId, setRecordId] = useState({});
    const [areOptionsLoading, setAreOptionsLoading] = useState(false);
    const [isFormSubmitting, setIsFormSubmitting] = useState(false);
    const [fetchedRecord, setFetchedRecord] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [errorColor, setErrorColor] = useState("");

    const formErrorState = useRef({});
    const printState = useRef({printCharacteristic: null, index: 0, data: null, canvas: null});

    const resetForm = function () {
        setMobileNo("");
        setFetchedRecord(null);
        setErrorMessage(null);
        setErrorColor("");
        setRecordId({});
        setAreOptionsLoading(false);
        setIsFormSubmitting(false);
        
        // Clear print content
        const printContent = document.getElementById("print-content");
        if (printContent) {
            printContent.innerHTML = "";
        }
        
        // Clear form errors
        formErrorState.current = {};
        
        // Clear print state
        printState.current = {printCharacteristic: null, index: 0, data: null, canvas: null};
    };

    const resetDom = function () {
        setFetchedRecord(null);
        //document.getElementById("print-content").innerHTML = "";
    };

    const searchByMobileNo = async (mobileNo) => {
        try {
            setAreOptionsLoading(true);
            setErrorMessage(null);
            resetDom();

            // Log the request details for debugging
            console.log("Making API request with mobile:", mobileNo);

            const response = await apiHelper.get({
                url: `${baseUrl}/.netlify/functions/event_attendance`,
                params: {search_text: mobileNo},
            });

            // Log the response for debugging
            console.debug("API Response:", JSON.stringify(response));

            if (response.status) {
                if (!response.json) {
                    throw new Error("Response json is undefined");
                }
                if (response.json.length === 0) {
                    setErrorMessage(
                        "Oops! It looks like you haven't completed the registration yet. Kindly do so. Hare Krishna."
                    );
                    setErrorColor("error"); // Material-UI color for error messages
                    return; // Early exit if no records found
                }
                console.log("response json: "+ response.json[0]["Record ID"])
                setRecordId(response.json[0]["Record ID"]);
                setFetchedRecord(response.json[0]);
            } else {
                setErrorMessage(`API returned status false: ${JSON.stringify(response.json)}`);
                setErrorColor("error");
            }
        } catch (error) {
            // Detailed error logging
            console.error("Error in searchByMobileNo:", {
                message: error.message,
                stack: error.stack,
                line: error.lineNumber,
                fileName: error.fileName,
            });
            setErrorMessage("An error occurred while searching.");
            setErrorColor("error");
        } finally {
            setAreOptionsLoading(false);
        }
    };

    const checkForFormErrors = (field, isError) => {
        formErrorState.current[field] = isError;
        return isError;
    };

    const handleError = function (error) {
        console.log(error);
        printState.current.printCharacteristic = null;
    };

    const connectToBluetooth = function () {
        navigator.bluetooth
        .requestDevice({
            filters: [
                {
                    services: ["000018f0-0000-1000-8000-00805f9b34fb"],
                },
            ],
        })
        .then((device) => {
            console.log("> Found " + device.name);
            console.log("Connecting to GATT Server...");
            const x = device.gatt.connect();
            return x;
        })
        .then((server) => {
            printState.current.server = server;
            console.log(server);
            console.log(server.connected);
            return server.getPrimaryService("000018f0-0000-1000-8000-00805f9b34fb");
        })
        .then((service) => service.getCharacteristic("00002af1-0000-1000-8000-00805f9b34fb"))
        .then((characteristic) => {
            // Cache the characteristic
            printState.current.printCharacteristic = characteristic;
            notify.success({description: "Connected successfully !!"});
        })
        .catch(handleError);
    };

    // Function to check if the pixel should be printed black
    const getDarkPixel = function (imageData, x, y) {
        // Return the pixels that will be printed black
        let red = imageData[(printState.current.canvas.width * y + x) * 4];
        let green = imageData[(printState.current.canvas.width * y + x) * 4 + 1];
        let blue = imageData[(printState.current.canvas.width * y + x) * 4 + 2];
        let alpha = imageData[(printState.current.canvas.width * y + x) * 4 + 3];
        if (alpha < 10) return 0;
        return red + green + blue > 730 ? 0 : 1;
    };
    const getImagePrintData = function (imageData) {
        if (imageData == null) {
            console.log("No image to print!");
            return new Uint8Array([]);
        }
        // Each 8 pixels in a row is represented by a byte
        let printData = new Uint8Array((printState.current.canvas.width / 8) * printState.current.canvas.height + 8);
        let offset = 0;
        // Set the header bytes for printing the image
        printData[0] = 29; // Print raster bitmap
        printData[1] = 118; // Print raster bitmap
        printData[2] = 48; // Print raster bitmap
        printData[3] = 0; // Normal 203.2 DPI
        printData[4] = printState.current.canvas.width / 8; // Number of horizontal printState.current.data bits (LSB)
        printData[5] = 0; // Number of horizontal printState.current.data bits (MSB)
        printData[6] = printState.current.canvas.height % 256; // Number of vertical printState.current.data bits (LSB)
        printData[7] = printState.current.canvas.height / 256; // Number of vertical printState.current.data bits (MSB)
        offset = 7;
        // Loop through image rows in bytes
        for (let i = 0; i < printState.current.canvas.height; ++i) {
            for (let k = 0; k < printState.current.canvas.width / 8; ++k) {
                let k8 = k * 8;
                //  Pixel to bit position mapping
                printData[++offset] =
                    getDarkPixel(imageData, k8 + 0, i) * 128 +
                    getDarkPixel(imageData, k8 + 1, i) * 64 +
                    getDarkPixel(imageData, k8 + 2, i) * 32 +
                    getDarkPixel(imageData, k8 + 3, i) * 16 +
                    getDarkPixel(imageData, k8 + 4, i) * 8 +
                    getDarkPixel(imageData, k8 + 5, i) * 4 +
                    getDarkPixel(imageData, k8 + 6, i) * 2 +
                    getDarkPixel(imageData, k8 + 7, i);
            }
        }
        return printData;
    };
    const sendImageData = function (imageData) {
        printState.current.index = 0;
        printState.current.data = getImagePrintData(imageData);
        return new Promise(function (resolve, reject) {
            sendNextImageDataBatch(resolve, reject);
        });
    };

    const sendNextImageDataBatch = function (resolve, reject) {
        // Can only write 512 bytes at a time to the characteristic
        // Need to send the image printState.current.data in 512 byte batches
        if (printState.current.index + 512 < printState.current.data.length) {
            printState.current.printCharacteristic
            .writeValueWithoutResponse(
                printState.current.data.slice(printState.current.index, printState.current.index + 512)
            )
            .then(() => {
                printState.current.index += 512;
                sendNextImageDataBatch(resolve, reject);
            })
            .catch((error) => reject(error));
        } else {
            // Send the last bytes
            if (printState.current.index < printState.current.data.length) {
                printState.current.printCharacteristic
                .writeValue(printState.current.data.slice(printState.current.index, printState.current.data.length))
                .then(() => {
                    resolve();
                })
                .catch((error) => reject(error));
            } else {
                resolve();
            }
        }
    };

    // Function triggered on clicking print slip
    const onClickPrintSlip = () => {
        console.log("on click print slip method get called..");

        if (
            !window.AndroidApp &&
            (!printState.current.printCharacteristic ||
                !(printState.current.server && printState.current.server.connected))
        ) {
            notify.error({description: "Connect to Printer first"});
            return;
        }

        if (Object.values(formErrorState.current).some((isError) => isError)) {
            notify.error({description: "Please fill all details"});
            return;
        }

        handlePrintSlip(fetchedRecord);
    };

    const handlePrintSlip = async (fetchedRecord) => {
        console.log("handle Print slip get called...");
        setIsFormSubmitting(true);

        try {
            document.querySelector("body").classList.add("print");
            document.getElementById("print-content").innerHTML = `
                <div style="width: 100%; height: 1px; background-color: black; margin-top: 4mm;"></div>
                <div style="display: flex; flex-direction: column; align-items: flex-start; margin-top: 7mm; font-size: 6mm;">
                    <div style="margin-bottom: 4mm;">
                        <span style="font-weight: bold;">Name:</span>
                        <span style="margin-left: 8px;">${fetchedRecord["Name"] || ""}</span>
                    </div>

                    <div style="margin-bottom: 4mm;">
                        <span style="font-weight: bold;">E.Name:</span>
                        <span style="margin-left: 8px;">${fetchedRecord["Event Name"]}</span>
                    </div>
                    
                    <div style="margin-bottom: 4mm;">
                        <span style="font-weight: bold;">F.Guide:</span>
                        <span style="margin-left: 8px;">${fetchedRecord["FG Name"]}</span>
                    </div>

                    <div style="margin-bottom: 4mm;">
                        <span style="font-weight: bold;">Guide No.:</span>
                        <span style="margin-left: 8px;">${fetchedRecord["FG Mobile"]}</span>
                    </div>
                    
                    <div style="margin-bottom: 4mm;">
                        <span style="font-weight: bold;">Date:</span>
                        <span style="margin-left: 8px;">${dateStr}</span>
                    </div>

                    <div style="margin-bottom: 4mm;">
                        <span style="font-weight: bold;">Time:</span>
                        <span style="margin-left: 8px;">${timeStr.toUpperCase()}</span>
                    </div>
                </div>

                <div style="text-align: center; margin-top: 10mm; font-size: 6mm; font-weight: bold;">
                    Hare Krishna !!<br/>Have a nice Event
                </div>

                <br/><br/><br/><br/>
            `;

            document.getElementById("print-slip").classList.add("print");
            const printNode = document.getElementById("print-slip");
            
            const canvas = await html2canvas(printNode, {
                width: printNode.offsetWidth,
                height: printNode.offsetHeight + 100,
                scale: 1,
            });
            
            printState.current.canvas = canvas;
            let context = printState.current.canvas.getContext("2d");
            const imageDataUrl = canvas.toDataURL("image/jpeg").split(";base64,")[1];
            //console.log(imageDataUrl);
            
            // Send the image data to the Android app or backend
            if (window.AndroidApp) {
                window.AndroidApp.printPrasadam(imageDataUrl);
            } else if (printState.current.printCharacteristic) {
                let imageData = context.getImageData(0, 0, canvas.width, canvas.height).data;
                await sendImageData(imageData);
            } else {
                console.log("Bluetooth printer not connected - skipping print for local testing");
            }

            // Mark attendance after successful printing
            await markAttendance(getRecordId);
            
        } catch (error) {
            console.error("Error in handlePrintSlip:", error);
            alert("Printing failed. Please try again.");
        } finally {
            // Clean up
            document.querySelector("body").classList.remove("print");
            document.getElementById("print-slip").classList.remove("print");
            document.getElementById("print-content").innerHTML = "";
            setIsFormSubmitting(false);
            resetForm();
        }
    };

    const markAttendance = async (recordId) => {
        console.log("markAttendance called...");
        console.log("Record ID:", recordId);

        const payload = {
            record_id: Array.isArray(recordId) ? recordId[0] : recordId,
            event_name: "Rang Barase 2026",
        };

        console.log("Payload:", payload);

        try {
            const response = await apiHelper.post({
                url: `${baseUrl}/.netlify/functions/event_attendance`,
                data: payload,
            });

            if (response.status) {
                console.log("Attendance marked successfully");
                notify.success({description: "Attendance recorded successfully!"});
                resetForm();
            } else {
                console.error("Error marking attendance:", response);
                notify.error({description: "Failed to record attendance. Please try again."});
            }
        } catch (error) {
            console.error("Error in markAttendance:", error);
            notify.error({description: "Something went wrong while recording attendance."});
        }
    };

    const currTime = new Date();
    const dateStr = currTime.toLocaleString("en-IN", {
        month: "long",
        day: "2-digit",
    });
    const timeStr = currTime.toLocaleString("en-IN", {
        hour12: true,
        hour: "2-digit",
        minute: "2-digit",
    });

    return (
        <Box sx={{display: "flex", flexDirection: "row", justifyContent: "center", padding: "15px"}}>
            <ColumnBox>
                <SmallHeading sx={{alignSelf: "center"}}>FOLK Explosion Event</SmallHeading>
                <ColumnBox sx={{alignItems: "center", maxWidth: "500px", width: "100%"}}>
                    {/* Row for TextField, Circular Progress, and Button */}
                    <RowBox
                        sx={{
                            width: "100%",
                            marginTop: "15px",
                            alignItems: "center", // Align vertically
                            justifyContent: "space-between", // Space out TextField and Button
                        }}
                    >
                        {/* Container for TextField and Spinner */}
                        <Box sx={{width: "85%", display: "flex", alignItems: "center"}}>
                            <TextField
                                required
                                label="Mobile No."
                                autoFocus
                                size="small"
                                value={mobileNo}
                                onChange={(event) => setMobileNo(event.target.value)}
                                error={checkForFormErrors("mobileNo", !mobileNo)}
                                sx={{flexGrow: 1, marginLeft: "30px"}} // Take up available space
                            />

                            {/* Circular Progress or Mobile Icon beside TextField */}
                            <Box sx={{marginLeft: "10px"}}>
                                {areOptionsLoading ? (
                                    <CircularProgress size="1.5rem" />
                                ) : (
                                    <MobileFriendlyIcon size="1.5rem" />
                                )}
                            </Box>
                        </Box>
                    </RowBox>

                    {/* Search Button */}
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => searchByMobileNo(mobileNo)}
                        disabled={!mobileNo || areOptionsLoading}
                        sx={{
                            minWidth: "120px",
                            marginTop: "15px", // Space between the input and button
                        }}
                    >
                        Search
                    </Button>

                    {!window.AndroidApp && (
                        <Button sx={{marginTop: "15px"}} onClick={() => connectToBluetooth()} variant="outlined">
                            Connect Printer
                        </Button>
                    )}
                    {/* Attendance Slip Section */}
                    <div id="print-slip" style={{textAlign: "center", marginTop: "20px", width: "100%"}}>
                        <img src="/images/folklogo.png" alt="FOLK Logo" style={{width: "45mm"}} />
                        <div
                            style={{
                                fontSize: "6.2mm",
                                fontWeight: 700,
                                letterSpacing: "0.10em",
                                textAlign: "center",
                            }}
                        >
                            RANG BARASE 2026
                        </div>
                        <div
                            id="print-content"
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "flex-start",
                                alignItems: "center",
                                background: "white",
                                fontSize: "5.5mm",
                                letterSpacing: "0.15em",
                                // paddingTop: "1mm",
                                // width: "360px",
                            }}
                        ></div>
                    </div>
                    <hr style={{width: "100%", border: "none", borderTop: "1px solid #000", margin: "4mm 0"}} />

                    {errorMessage && (
                        <Snackbar
                            open={!!errorMessage}
                            autoHideDuration={8000}
                            onClose={() => setErrorMessage(null)}
                            anchorOrigin={{vertical: "bottom", horizontal: "center"}} // Center the Snackbar
                        >
                            <Alert severity={errorColor} onClose={() => setErrorMessage(null)} sx={{width: "100%"}}>
                                {errorMessage}
                            </Alert>
                        </Snackbar>
                    )}

                    {/* Display fetched record */}
                    {fetchedRecord && (
                        <Box sx={{textAlign: "left", fontSize: "5.2mm", marginLeft: "20px"}}>
                            <div style={{marginBottom: "4mm"}}>
                                <span style={{fontWeight: "500", marginRight: "4px"}}>Name:</span>
                                {fetchedRecord["Name"]}
                            </div>
                            <div style={{marginBottom: "4mm"}}>
                                <span style={{fontWeight: "500", marginRight: "4px"}}>FOLK Guide:</span>
                                {fetchedRecord["FG Name"] || ""}
                            </div>
                            <div style={{marginBottom: "4mm"}}>
                                <span style={{fontWeight: "500", marginRight: "4px"}}>Event Name:</span>
                                {fetchedRecord["Event Name"] || ""}
                            </div>
                            <div style={{marginBottom: "4mm"}}>
                                <span style={{fontWeight: "500", marginRight: "4px"}}>Occupation:</span>
                                {fetchedRecord["Occupation"] || ""}
                            </div>

                            <div style={{marginBottom: "4mm"}}>
                                <span style={{fontWeight: "500", marginRight: "4px"}}>Donation Paid:</span>
                                {fetchedRecord["Payment Status"]}
                            </div>

                            <div style={{marginBottom: "4mm"}}>
                                <span style={{fontWeight: "500", marginRight: "4px"}}>Donation Amount:</span>
                                {fetchedRecord["Amount"] || ""}
                            </div>

                            <div style={{marginBottom: "4mm"}}>
                                <span style={{fontWeight: "500", marginRight: "4px"}}>Registration Date:</span>
                                {fetchedRecord["Timestamp"] ? new Date(fetchedRecord["Timestamp"]).toLocaleDateString("en-GB") : ""}
                            </div>
                        </Box>
                    )}
                    <RowBox
                        sx={{width: "100%", marginTop: "15px", alignItems: "center", justifyContent: "space-around"}}
                    >
                        {isFormSubmitting ? (
                            <CircularProgress sx={{marginTop: "15px"}} />
                        ) : (
                            <Button sx={{marginTop: "15px"}} onClick={onClickPrintSlip} variant="outlined">
                                Print Slip
                            </Button>
                        )}
                    </RowBox>
                </ColumnBox>
            </ColumnBox>
        </Box>
    );
}
